import axios from 'axios'
import http from '../services/httpServiceHealthCare'

const API_URL = '/TwilioRecordings'

/** URL สำหรับยิง moveRecording โดยตรง (ทดสอบ local) - ใช้จาก env หรือ derive จาก base */
function getMoveRecordingTestUrl() {
  const envUrl = process.env.REACT_APP_MOVE_RECORDING_TEST_URL
  if (envUrl) return envUrl
  const base = process.env.REACT_APP_API_URL_HEALTH_CARE || ''
  const root = base.replace(/\/api\/?$/, '') || 'http://localhost:3002'
  return `${root}/internal/jobs/moveRecording`
}

export const getSummary = async () => {
  const response = await http.get(`${API_URL}/summary`)
  return response
}

/**
 * ดึง TwilioRecordings ทั้งหมดในช่วงวันที่ ด้วย JSON filter + filter[skip] เพื่อให้ pagination ทำงานจริง
 * (เก่าใช้ ?page= ซึ่ง LoopBack ignore -> ได้ข้อมูล page เดิมซ้ำ)
 */
export const fetchAllInRange = async ({ dateFrom, dateTo, pageSize = 500, maxPages = 100 } = {}) => {
  const all = []
  let skip = 0
  for (let p = 0; p < maxPages; p += 1) {
    const filter = {
      limit: pageSize,
      skip,
      order: 'dateCreated DESC',
      where: {},
    }
    if (dateFrom) filter.where.dateCreated = { ...filter.where.dateCreated, gte: new Date(dateFrom).toISOString() }
    if (dateTo) filter.where.dateCreated = { ...filter.where.dateCreated, lte: new Date(dateTo).toISOString() }
    if (!Object.keys(filter.where).length) delete filter.where
    // eslint-disable-next-line no-await-in-loop
    const res = await http.get(`${API_URL}?filter=${encodeURIComponent(JSON.stringify(filter))}`)
    const body = res && res.data ? res.data : res
    const items = Array.isArray(body && body.data) ? body.data : Array.isArray(body) ? body : []
    if (!items.length) break
    all.push(...items)
    if (items.length < pageSize) break
    skip += items.length
  }
  return all
}

export const getList = async (params = {}) => {
  const { state, roomSid, dateFrom, dateTo, q, page = 1, limit = 20, sort } = params
  const query = {
    page,
    'filter[limit]': limit,
    'filter[order]': sort || 'dateCreated DESC'
  }
  if (state) query['filter[where][state]'] = state
  if (roomSid) query['filter[where][roomSid]'] = roomSid
  if (dateFrom) query['filter[where][dateCreated][gte]'] = new Date(dateFrom).toISOString()
  if (dateTo) query['filter[where][dateCreated][lte]'] = new Date(dateTo).toISOString()
  if (q) {
    query['filter[where][or][0][recordingSid][like]'] = q
    query['filter[where][or][0][recordingSid][options]'] = 'i'
    query['filter[where][or][1][roomSid][like]'] = q
    query['filter[where][or][1][roomSid][options]'] = 'i'
  }
  const searchParams = new URLSearchParams(query).toString()
  const response = await http.get(`${API_URL}?${searchParams}`)
  return response
}

export const sync = async (body) => {
  const response = await http.post(`${API_URL}/sync`, body)
  return response
}

/**
 * Sync จาก Twilio แบบแบ่งชุด (รองรับ 80K+)
 * ส่ง pageToken, batchSize เป็น query; คืน nextPageToken ให้เรียกซ้ำจนครบ
 */
export const syncAll = async (pageToken, batchSize = 2000) => {
  const params = new URLSearchParams()
  if (pageToken) params.set('pageToken', pageToken)
  if (batchSize != null) params.set('batchSize', String(batchSize))
  const qs = params.toString()
  const url = qs ? `${API_URL}/sync-all?${qs}` : `${API_URL}/sync-all`
  const response = await http.post(url)
  return response
}

export const queueMove = async (id) => {
  const response = await http.post(`${API_URL}/${encodeURIComponent(id)}/queue-move`)
  return response
}

export const bulkQueueMove = async (ids) => {
  const response = await http.post(`${API_URL}/bulk-queue-move`, { ids })
  return response
}

/**
 * Sync จาก Firebase Storage: สแกนไฟล์ใน GCS แล้วอัปเดต DB เป็น UPLOADED
 * ใช้เมื่อไฟล์ขึ้น storage แล้วแต่ DB ไม่อัปเดต
 * ส่ง batchSize, pageToken; คืน nextPageToken ให้เรียกซ้ำจนครบ
 */
/**
 * สแกน GCS หา recording ที่อัปโหลดซ้ำ
 */
export const checkDuplicateUploads = async () => {
  const response = await http.get(`${API_URL}/check-duplicate-uploads`)
  return response
}

/**
 * สแกน GCS หา recording ที่ซ้ำ แล้วลบไฟล์ซ้ำออก
 */
export const checkAndDeleteDuplicates = async () => {
  const response = await http.post(`${API_URL}/check-and-delete-duplicates`)
  return response
}

/**
 * รีเซ็ต state QUEUED ทั้งหมดเป็น FAILED (ให้ตัวเลข QUEUED ใน Summary เป็น 0)
 */
export const resetQueuedToFailed = async () => {
  const response = await http.post(`${API_URL}/reset-queued-to-failed`)
  return response
}

export const syncFromStorage = async (batchSize = 1000, pageToken) => {
  const params = new URLSearchParams()
  if (batchSize != null) params.set('batchSize', String(batchSize))
  if (pageToken) params.set('pageToken', pageToken)
  const qs = params.toString()
  const url = qs ? `${API_URL}/sync-from-storage?${qs}` : `${API_URL}/sync-from-storage`
  const response = await http.post(url)
  return response
}

/**
 * Queue ทั้งหมดที่ eligible (DISCOVERED/FAILED) เข้า GCS แบบแบ่งชุด
 * ส่ง skip, batchSize; คืน nextSkip ให้เรียกซ้ำจนครบ
 */
export const queueMoveAll = async (skip, batchSize = 500) => {
  const params = new URLSearchParams()
  if (skip != null) params.set('skip', String(skip))
  if (batchSize != null) params.set('batchSize', String(batchSize))
  const qs = params.toString()
  const url = qs ? `${API_URL}/queue-move-all?${qs}` : `${API_URL}/queue-move-all`
  const response = await http.post(url)
  return response
}

/**
 * Re-queue รายการที่ค้างใน state QUEUED (สร้าง Cloud Task ใหม่)
 * ส่ง skip, batchSize; คืน nextSkip ให้เรียกซ้ำจนครบ
 */
export const requeueQueuedAll = async (skip, batchSize = 500) => {
  const params = new URLSearchParams()
  if (skip != null) params.set('skip', String(skip))
  if (batchSize != null) params.set('batchSize', String(batchSize))
  const qs = params.toString()
  const url = qs ? `${API_URL}/requeue-queued-all?${qs}` : `${API_URL}/requeue-queued-all`
  const response = await http.post(url)
  return response
}

export const retry = async (id) => {
  const response = await http.post(`${API_URL}/${encodeURIComponent(id)}/retry`)
  return response
}

export const deleteTwilio = async (id) => {
  const response = await http.post(`${API_URL}/${encodeURIComponent(id)}/delete-twilio`)
  return response
}

/**
 * ยิง moveRecording โดยตรงไปที่ internal endpoint (สำหรับทดสอบ local)
 * ไม่ผ่าน Cloud Tasks, ไม่ผ่าน queueMove - ยิงตรงไป processMoveJob
 */
export const fireMoveRecordingTest = async (recordingSid) => {
  const url = getMoveRecordingTestUrl()
  const headers = { 'Content-Type': 'application/json' }
  const secret = process.env.REACT_APP_MOVE_RECORDING_TEST_SECRET
  if (secret) headers['X-Internal-Secret'] = secret
  const response = await axios.post(url, { recordingSid }, { headers })
  return response
}
